import React, { useState, useEffect } from 'react';
import { saveShift, getShifts, deleteShift, Shift } from '../services/shiftService';
import { Trash2 } from 'lucide-react';

interface Props {
  tenantId: string;
}

export const ShiftSettingsForm: React.FC<Props> = ({ tenantId }) => {
  const [formData, setFormData] = useState({
    shiftName: '',
    startTime: '',
    endTime: ''
  });
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [status, setStatus] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const fetchShifts = async () => {
    const data = await getShifts(tenantId);
    setShifts(data);
  };

  useEffect(() => {
    fetchShifts();
  }, [tenantId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await saveShift(tenantId, {
        tenantId,
        ...formData
      });
      setStatus('Shift saved successfully!');
      setFormData({
        shiftName: '',
        startTime: '',
        endTime: ''
      });
      fetchShifts();
    } catch (error) {
      setStatus('Error saving shift.');
    }
  };

  const handleDelete = async (shiftId: string) => {
    try {
      await deleteShift(tenantId, shiftId);
      setStatus('Shift deleted successfully!');
      fetchShifts();
      setDeleteConfirmId(null);
    } catch (error) {
      setStatus('Error deleting shift.');
      setDeleteConfirmId(null);
    }
  };

  return (
    <div className="space-y-8 md:space-y-12 max-w-4xl mx-auto">
      <form onSubmit={handleSubmit} className="border-b border-gray-50 pb-8">
        <h2 className="text-xl md:text-3xl font-black text-gray-900 tracking-tight uppercase font-sans mb-4">Shift Configuration</h2>

        {/* Operational Guidance Callout */}
        <div className="mb-6 p-4 bg-blue-50/80 border border-blue-200/80 rounded-2xl flex items-start gap-3.5 text-blue-950">
          <span className="text-xl leading-none mt-0.5 select-none">💡</span>
          <div className="text-xs space-y-1">
            <p className="font-bold text-sm text-blue-900 tracking-tight">Best Practice: Match Shift Names With Your POS System</p>
            <p className="text-blue-800 leading-relaxed">
              For accurate tip distributions and shift-based allocations, ensure the <strong>Shift Names</strong> and <strong>Times</strong> configured here match the service periods or shift labels in your POS (e.g., Toast, Square, Clover).
            </p>
            <p className="text-blue-700 text-[11px] leading-relaxed">
              TipValue uses these shift names to link imported POS clock-in hours with your shared and pooled tip rules. Consistent naming ensures every team member's hours and tip shares are matched cleanly.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Shift Name</label>
            <input type="text" placeholder="e.g. Lunch" value={formData.shiftName} onChange={e => setFormData({...formData, shiftName: e.target.value})} className="input-field" required />
            <p className="text-[10px] text-gray-500 font-medium">Use the exact shift label from your POS reports (e.g., Lunch, Dinner).</p>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Start Time</label>
            <input type="time" value={formData.startTime} onChange={e => setFormData({...formData, startTime: e.target.value})} className="input-field" required />
            <p className="text-[10px] text-gray-400">Scheduled shift start</p>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">End Time</label>
            <input type="time" value={formData.endTime} onChange={e => setFormData({...formData, endTime: e.target.value})} className="input-field" required />
            <p className="text-[10px] text-gray-400">Scheduled shift end</p>
          </div>
        </div>
        <button type="submit" className="btn-primary w-full md:w-auto md:px-12">Register Shift</button>
        {status && <p className="mt-4 text-sm font-medium text-blue-600 animate-in fade-in">{status}</p>}
      </form>

      <div>
        <h3 className="text-lg md:text-2xl font-black text-gray-900 tracking-tight uppercase font-sans mb-4">Active Schedule</h3>
        <div className="overflow-hidden border border-gray-100 rounded-2xl">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50/50">
                <th className="px-6 py-4 font-bold text-gray-400 text-[10px] uppercase tracking-widest">Label</th>
                <th className="px-6 py-4 font-bold text-gray-400 text-[10px] uppercase tracking-widest">Starts</th>
                <th className="px-6 py-4 font-bold text-gray-400 text-[10px] uppercase tracking-widest">Ends</th>
                <th className="px-6 py-4 font-bold text-gray-400 text-[10px] uppercase tracking-widest text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {shifts.map((shift) => (
                <tr key={shift.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-4 font-bold text-gray-900">{shift.shiftName}</td>
                  <td className="px-6 py-4 text-gray-600">{shift.startTime}</td>
                  <td className="px-6 py-4 text-gray-600">{shift.endTime}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end">
                      {deleteConfirmId === shift.id ? (
                        <div className="flex gap-2">
                          <button onClick={() => setDeleteConfirmId(null)} className="text-xs text-gray-500">Cancel</button>
                          <button onClick={() => handleDelete(shift.id!)} className="text-xs text-red-600 font-bold">Confirm</button>
                        </div>
                      ) : (
                        <button 
                          onClick={() => setDeleteConfirmId(shift.id!)}
                          className="text-red-500 hover:text-red-700 p-2 cursor-pointer"
                          type="button"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
